import { useMatch } from "react-router";
import {
  type ParameterizedRoute,
  RouteParameterParseError,
} from "../constants/routes";

/**
 * Reads and parses parameters when the supplied route matches the location.
 */
export function useRouteParams<
  Path extends string,
  Params extends Readonly<Record<string, unknown>>,
  InputParams extends Readonly<Record<string, unknown>>,
>(route: ParameterizedRoute<Path, Params, InputParams>): Params | null {
  return useMatchedRouteParams(route, true);
}

/**
 * Reads parameters from an ancestor route while one of its descendants is
 * active. Prefer useRouteParams unless the caller is part of a parent layout.
 */
export function useAncestorRouteParams<
  Path extends string,
  Params extends Readonly<Record<string, unknown>>,
  InputParams extends Readonly<Record<string, unknown>>,
>(route: ParameterizedRoute<Path, Params, InputParams>): Params | null {
  return useMatchedRouteParams(route, false);
}

function useMatchedRouteParams<
  Path extends string,
  Params extends Readonly<Record<string, unknown>>,
  InputParams extends Readonly<Record<string, unknown>>,
>(
  route: ParameterizedRoute<Path, Params, InputParams>,
  end: boolean,
): Params | null {
  const selectedMatch = useMatch({ path: route.pattern, end });

  if (!selectedMatch) return null;

  try {
    return route.parseParams(
      selectedMatch.params as Parameters<typeof route.parseParams>[0],
    );
  } catch (error) {
    // React Router can independently match a dynamic pattern even when it
    // selected a static sibling. An invalid codec value means this descriptor
    // is not the applicable route for an optional lookup.
    if (error instanceof RouteParameterParseError) return null;

    throw error;
  }
}
